import React, { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faSliders } from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { WebsiteBuilderRange } from "pages/secure/facility/websiteBuilder/editor/components/range/WebsiteBuilderRange";
import {
  EMAIL_DOCUMENT_LINE_HEIGHT_STEP,
  EMAIL_DOCUMENT_MAX_LINE_HEIGHT,
  EMAIL_DOCUMENT_MIN_LINE_HEIGHT,
} from "../../../schema/emailDocumentContract";
import {
  EmailBuilderBlockSettingsIconButton,
} from "./EmailBuilderBlockSettingsShared";
import { useEmailBuilderContinuousHistoryGroup } from "./emailBuilderContinuousHistoryGroup";
import { emailBuilderSettingsAutosaveGestureHandlers } from "../../../emailBuilderSettingsTextAutosaveDeferral";
import { useStableDomId, useStableScopedDomId } from "../../../useStableDomId";

const LINE_HEIGHT_PRESETS = [
  { value: 1.2, labelKey: "Tight" },
  { value: 1.5, labelKey: "Normal" },
  { value: 1.8, labelKey: "Relaxed" },
  { value: 2, labelKey: "Loose" },
] as const;

type LineHeightPreset = (typeof LINE_HEIGHT_PRESETS)[number]["value"];

const LINE_HEIGHT_ICON_LINES: Record<LineHeightPreset, readonly number[]> = {
  1.2: [7, 8.5],
  1.5: [5, 11],
  1.8: [4, 8, 12],
  2: [3, 8, 13],
};

function isPresetLineHeight(value: number): value is LineHeightPreset {
  return LINE_HEIGHT_PRESETS.some(preset => Math.abs(preset.value - value) < 0.01);
}

function LineHeightIcon({ value }: { value: LineHeightPreset }) {
  const lines = LINE_HEIGHT_ICON_LINES[value];

  return (
    <svg width="16" height="16" viewBox="0 0 16 16" className="email-builder-line-height-icon" aria-hidden>
      {lines.map(y => (
        <line
          key={y}
          x1="2"
          y1={y}
          x2="14"
          y2={y}
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

type LineHeightOptionButtonProps = {
  tooltip: string;
  selected: boolean;
  ariaLabel: string;
  onClick: () => void;
  children: ReactNode;
};

function LineHeightOptionButton(props: LineHeightOptionButtonProps) {
  const { tooltip, selected, ariaLabel, onClick, children } = props;
  const tooltipId = useStableScopedDomId("email-builder-line-height-option", ariaLabel);

  return (
    <OverlayTrigger
      placement="bottom"
      delay={{ show: 400, hide: 100 }}
      overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}
    >
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        aria-label={ariaLabel}
        data-on={selected ? "true" : "false"}
        onClick={onClick}
      >
        {children}
      </button>
    </OverlayTrigger>
  );
}

export type TEmailBuilderLineHeightControlProps = {
  value: number;
  defaultValue: number;
  onChange: (value: number, historyGroupId?: string | null) => void;
};

export function EmailBuilderLineHeightControl(props: TEmailBuilderLineHeightControlProps) {
  const { t } = useTranslation();
  const { value, defaultValue, onChange } = props;
  const labelId = useStableDomId("email-builder-line-height-label");
  const continuousHistory = useEmailBuilderContinuousHistoryGroup();
  const autosaveGestureHandlers = emailBuilderSettingsAutosaveGestureHandlers(continuousHistory);
  const [customModePinned, setCustomModePinned] = useState(!isPresetLineHeight(value));
  const activePreset = customModePinned
    ? undefined
    : LINE_HEIGHT_PRESETS.find(preset => Math.abs(preset.value - value) < 0.01);
  const isCustomActive = customModePinned;
  const showReset = Math.abs(value - defaultValue) >= 0.01;

  return (
    <div className="email-builder-block-settings-section email-builder-line-height-control">
      <div className="email-builder-block-settings-color-row">
        <div id={labelId} className="email-builder-block-settings-label">
          {t("Line height")}
        </div>
        <div className="email-builder-block-settings-label-row-actions email-builder-block-settings-label-row-actions--slots-1">
          {showReset ? (
            <EmailBuilderBlockSettingsIconButton
              label={t("Reset to default")}
              icon={faRotateLeft}
              onClick={() => {
                setCustomModePinned(!isPresetLineHeight(defaultValue));
                onChange(defaultValue, null);
              }}
            />
          ) : (
            <span className="email-builder-block-settings-icon-button-slot" aria-hidden="true" />
          )}
        </div>
      </div>
      <div
        className="email-builder-settings-segment-wide email-builder-settings-segment-wide--with-icons"
        role="radiogroup"
        aria-labelledby={labelId}
      >
        {LINE_HEIGHT_PRESETS.map(preset => {
          const selected = !isCustomActive && activePreset?.value === preset.value;
          const presetLabel = t(preset.labelKey);

          return (
            <LineHeightOptionButton
              key={preset.value}
              tooltip={presetLabel}
              selected={selected}
              ariaLabel={t("{{label}}, line height {{value}}", {
                label: presetLabel,
                value: preset.value,
              })}
              onClick={() => {
                setCustomModePinned(false);
                onChange(preset.value);
              }}
            >
              <LineHeightIcon value={preset.value} />
            </LineHeightOptionButton>
          );
        })}
        <LineHeightOptionButton
          tooltip={t("Custom")}
          selected={isCustomActive}
          ariaLabel={t("Custom line height")}
          onClick={() => setCustomModePinned(true)}
        >
          <FontAwesomeIcon icon={faSliders} aria-hidden="true" />
        </LineHeightOptionButton>
      </div>
      {isCustomActive ? (
        <div className="email-builder-line-height-value-row">
          <WebsiteBuilderRange
            min={EMAIL_DOCUMENT_MIN_LINE_HEIGHT}
            max={EMAIL_DOCUMENT_MAX_LINE_HEIGHT}
            step={EMAIL_DOCUMENT_LINE_HEIGHT_STEP}
            value={value}
            setValue={next => {
              onChange(next, continuousHistory.noteChange());
            }}
            {...autosaveGestureHandlers}
          />
        </div>
      ) : null}
    </div>
  );
}
